/**
 * StatGrid / StatTile — the counts a screen is about: a big `figure`, then the
 * `label` that says what it counts, and an optional `note` (`meta`, one line)
 * that puts it in context ("+4% vs same days last month"). An even grid: 3
 * across on a phone (a long label spans 2, the last tile fills its row), one
 * row on wider screens, or a fixed `columns` count (a narrow side panel). The
 * `warning` tile (a count that needs action) turns its figure and outline
 * `warning`. Compact density (a DensityProvider, or `density`) uses the 20px
 * compact figure and tighter padding (`compactSpec`).
 *
 * `variant="strip"` (dashboards): ONE card with the figures side by side and
 * thin dividers between them, 2 across on a phone, or a fixed `columns` count
 * (the last figure stretches to fill its row, so no row is left part-empty).
 * Short labels; a figure's explanation is its tooltip (`title`), or with
 * `help` the tile is a button that opens the full explanation (MetricHelp) —
 * no info icon crowding the label. Notes wrap rather than being cut off; a
 * `noteTone` colours one (up / down against a past figure, or a warning). A
 * `warning` figure is told by its amber value alone, as in the app: no tint
 * or edge, which belong to the section bands below (a tinted, edged tile read
 * as one of them). A tile with `onPress` is a button that opens what it counts.
 */
import React from 'react';
import { useResponsive } from '@turn-connect/hooks';
import {
  compactSpec,
  fixedTileSpans,
  statTileSpans,
  textRole,
  tokenVars,
  type AccentKey,
  type Density,
  type MetricExplanation,
} from '@turn-connect/ui';
import { AccentIcon } from './AccentIcon';
import { MetricHelp } from './MetricHelp';
import { useDensity } from './density';
import { figureStyle } from './SectionFigure';

export interface StatTileProps {
  value: React.ReactNode;
  label: string;
  /** One muted line under the label, cut with an ellipsis when long (in a strip it wraps). */
  note?: string;
  /** Colours the note: `up` / `down` against a past figure, `warning` when it asks for action. */
  noteTone?: 'up' | 'down' | 'warning';
  /** Strip: the tile is a button that opens this explanation (what the figure means, its numbers). */
  help?: MetricExplanation;
  testId?: string;
  warning?: boolean;
  /** The part's icon and color, repeated in its section's header band. */
  accent?: AccentKey;
  /** False greys the icon (nothing waiting). */
  accentActive?: boolean;
  /** Grid columns this tile spans. */
  span?: number;
  /** Opens what the figure counts: the tile becomes a button. */
  onPress?: () => void;
  /** The figure's full wording, as a tooltip. */
  title?: string;
  /** Nothing to show (zero, no data yet): the figure is muted. */
  quiet?: boolean;
  /** `compact`: the 20px figure and tighter padding. Defaults to the nearest DensityProvider. */
  density?: Density;
}

/** Hover and focus for pressable tiles (inline styles cannot express them). */
const TILE_CSS = `.tc-stat-press { cursor: pointer; }
@media (hover: hover) { .tc-stat-press:hover { background-image: linear-gradient(${tokenVars.wash}, ${tokenVars.wash}); } }
.tc-stat-press:focus-visible { outline: 2px solid ${tokenVars.focus}; outline-offset: -2px; }`;

type TileLook = { strip: boolean; dividerLeft?: boolean; dividerTop?: boolean };

const NOTE_COLORS = { up: tokenVars.success, down: tokenVars.warningText, warning: tokenVars.warningText } as const;

/** A help tile's button: the click reaches its MetricHelp, which opens the tip. */
const NO_OP = () => undefined;

const tileStyle = ({ warning, span = 1 }: StatTileProps, look: TileLook, compact: boolean): React.CSSProperties =>
  look.strip
    ? {
        gridColumn: span > 1 ? `span ${span}` : undefined,
        background: 'transparent',
        borderLeft: look.dividerLeft ? `1px solid ${tokenVars.borderSubtle}` : undefined,
        borderTop: look.dividerTop ? `1px solid ${tokenVars.borderSubtle}` : undefined,
        padding: `${compactSpec.tile.paddingVertical}px ${compactSpec.tile.paddingHorizontal}px`,
        minHeight: 44,
        display: 'grid',
        gap: 1,
        alignContent: 'start',
        minWidth: 0,
      }
    : {
        gridColumn: span > 1 ? `span ${span}` : undefined,
        background: tokenVars.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: warning ? tokenVars.warning : tokenVars.borderSubtle,
        borderRadius: 8,
        padding: compact ? `${compactSpec.tile.paddingVertical}px ${compactSpec.tile.paddingHorizontal}px` : '12px 16px',
        display: 'grid',
        gap: compact ? 2 : 4,
        alignContent: 'start',
        minWidth: 0,
      };

/** A help tile's MetricHelp says on the tile's button whether its tip is open, and points to it. */
type TipState = { 'aria-expanded'?: boolean; 'aria-describedby'?: string };

const StatTileInner: React.FC<StatTileProps & TipState & { look: TileLook }> = (props) => {
  const { value, label, note, noteTone, help, testId, warning, accent, accentActive = true, onPress, title, quiet, density, look } = props;
  const ownDensity = useDensity(density);
  const compact = look.strip || ownDensity === 'compact';
  const figureColor = warning ? (look.strip ? tokenVars.warningText : tokenVars.warning) : quiet ? tokenVars.text.muted : undefined;
  const valueStyle: React.CSSProperties = {
    ...figureStyle(compact, figureColor),
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const body = (
    <>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
        <span data-testid={testId ? `${testId}-value` : undefined} style={valueStyle}>
          {value}
        </span>
        {accent && <AccentIcon accent={accent} active={accentActive} testId={testId ? `${testId}-icon` : undefined} />}
      </span>
      {/* The strip's buttons are named by their words ("20 In shop"). */}
      {look.strip ? ' ' : null}
      <span style={textRole('label')}>{label}</span>
      {note && (
        <span
          data-testid={testId ? `${testId}-note` : undefined}
          title={look.strip ? undefined : note}
          style={{
            ...textRole('meta', noteTone ? NOTE_COLORS[noteTone] : undefined),
            ...(look.strip ? { overflowWrap: 'anywhere' } : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
          }}
        >
          {note}
        </span>
      )}
    </>
  );
  const style = tileStyle(props, look, compact);
  const common = { 'data-testid': testId, 'data-warning': warning ? 'true' : undefined, title };
  if (help) {
    // The whole tile opens its explanation (press, not hover: pointing across
    // the strip should not pop tips); the tip is the tile's own button.
    return (
      <MetricHelp
        icon={false}
        openOn="press"
        label={`About ${label}`}
        explanation={help}
        style={{ display: 'grid', alignItems: 'stretch', width: '100%', minWidth: 0 }}
      >
        <StatTileInner {...props} help={undefined} onPress={onPress ?? NO_OP} />
      </MetricHelp>
    );
  }
  if (onPress) {
    return (
      <button
        type="button"
        className="tc-stat-press"
        onClick={onPress}
        {...common}
        aria-expanded={props['aria-expanded']}
        aria-describedby={props['aria-describedby']}
        style={{
          ...style,
          ...(look.strip ? { border: 0, borderLeft: style.borderLeft, borderTop: style.borderTop } : null),
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          width: '100%',
        }}
      >
        {body}
      </button>
    );
  }
  return (
    <div {...common} style={style}>
      {body}
    </div>
  );
};

export const StatTile: React.FC<StatTileProps> = (props) => <StatTileInner {...props} look={{ strip: false }} />;

export interface StatGridProps {
  tiles: readonly Omit<StatTileProps, 'span'>[];
  testId?: string;
  /** `strip`: one card with thin dividers (dashboards). Default: separate tiles. */
  variant?: 'tiles' | 'strip';
  /** Names the figures for screen readers (strip: a list). */
  ariaLabel?: string;
  /**
   * A fixed column count. Tiles: a narrow panel; otherwise 3 on a phone and
   * one row when wide. Strip: the caller's breakpoint (4 across on a tablet);
   * otherwise 2 on a phone and one row when wide.
   */
  columns?: number;
}

export const StatGrid: React.FC<StatGridProps> = ({ tiles, testId, variant = 'tiles', ariaLabel, columns: fixedColumns }) => {
  const { isMobile } = useResponsive();
  if (variant === 'strip') {
    const columns = Math.max(1, Math.min(fixedColumns ?? (isMobile ? 2 : tiles.length), tiles.length));
    // A part-empty last row: its last figure stretches to fill it.
    const spans = fixedTileSpans(tiles.length, columns);
    return (
      <div
        data-testid={testId}
        style={{
          background: tokenVars.surface,
          border: `1px solid ${tokenVars.borderSubtle}`,
          borderRadius: 8,
          // clip, not hidden: a hidden box scrolls to bring a focused tile into
          // view, and that scroll would close the tip the tile just opened.
          overflow: 'clip',
          minWidth: 0,
        }}
      >
        <style>{TILE_CSS}</style>
        <div role="list" aria-label={ariaLabel} style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {tiles.map((t, i) => (
            <div
              key={t.label}
              role="listitem"
              style={{ display: 'grid', minWidth: 0, gridColumn: spans[i] > 1 ? `span ${spans[i]}` : undefined }}
            >
              <StatTileInner {...t} look={{ strip: true, dividerLeft: i % columns !== 0, dividerTop: i >= columns }} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  const columns = fixedColumns ?? (isMobile ? 3 : Math.max(1, tiles.length));
  // A fixed count has room for any label: every tile spans 1, the last fills its row.
  const spans = fixedColumns
    ? fixedTileSpans(tiles.length, columns)
    : statTileSpans(
        tiles.map((t) => t.label),
        columns
      );
  return (
    <div data-testid={testId} style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 8 }}>
      <style>{TILE_CSS}</style>
      {tiles.map((t, i) => (
        <StatTile key={t.label} {...t} span={spans[i]} />
      ))}
    </div>
  );
};

export default StatGrid;
